import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarHeart, Trash2 } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { useAdviserName } from "./AdviserCard";
import { money, shortDate } from "../lib/format";
import { useBasket } from "../context/BasketContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

interface SuggestedCase { id: string; items: { product_handle: string; variant_id: string; title: string; variant_title: string; image: string | null; unit_price_pence: number; quantity: number; line_total_pence: number }[]; total_pence: number; note: string; adviser: string; status: string; created_at: string; swap_requests?: string[] }
interface SpecialDate { id: string; label: string; person: string; date: string; year?: number | null; day?: number; month?: number }

export function SuggestedCases() {
  const qc = useQueryClient();
  const { add, setOpen } = useBasket();
  const { data } = useQuery<SuggestedCase[]>({ queryKey: ["cases"], queryFn: () => api.get("/cases").then((r) => r.data) });
  const nm = useAdviserName();
  const open = (data || []).filter((c) => c.status === "suggested");
  if (open.length === 0) return null;
  const swap = async (c: SuggestedCase, i: SuggestedCase["items"][number]) => {
    const note = window.prompt(`Swap ${i.title} — anything you'd prefer instead? (optional)`, "");
    if (note === null) return;
    try {
      await api.post(`/cases/${c.id}/swap`, { variant_id: i.variant_id, note });
      toast.success(`Swap requested — ${nm.who} will pick an alternative`);
      qc.invalidateQueries({ queryKey: ["cases"] });
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const approve = async (c: SuggestedCase) => {
    const collect = window.confirm(`Approve this case for ${money(c.total_pence)} from your cellar?\n\nOK = deliver to your most recent address · Cancel = choose collection in Melbourne instead`);
    try {
      const { data } = await api.post(`/cases/${c.id}/approve`, { delivery_method: collect ? "delivery" : "collection" });
      toast.success(`Order ${data.order.shopify_order_name || ""} placed — paid from your cellar`);
      ["cases", "wallet", "orders", "ledger"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const act = async (c: SuggestedCase, action: "accepted" | "dismissed") => {
    if (action === "accepted") {
      c.items.forEach((i) => add({ product_handle: i.product_handle, variant_id: i.variant_id, title: i.title, variant_title: i.variant_title, image: i.image, unit_price_pence: i.unit_price_pence }, i.quantity));
      setOpen(true);
    }
    await api.post(`/cases/${c.id}/${action}`).catch(() => undefined);
    qc.invalidateQueries({ queryKey: ["cases"] });
  };
  return (
    <section className="space-y-px" data-testid="suggested-cases">
      {open.map((c) => (
        <div key={c.id} className="border border-[var(--wine)] bg-[var(--wine)]/5 p-6" data-testid={`suggested-case-${c.id}`}>
          <div className="eyebrow !text-[var(--wine)]">A case from {nm.who} · {shortDate(c.created_at)}</div>
          {c.note && <p className="mt-2 font-serif text-lg italic">“{c.note}”</p>}
          <ul className="mt-3 space-y-1 text-sm">{c.items.map((i) => <li key={i.variant_id} className="flex items-center justify-between gap-3"><span>{i.quantity} × <Link to={`/shop/${i.product_handle}`} className="underline-offset-4 hover:underline">{i.title}</Link></span><span className="flex items-center gap-3"><span className="tabular-nums">{money(i.line_total_pence)}</span>{(c.swap_requests || []).includes(i.variant_id) ? <span className="text-[10px] uppercase tracking-widest text-neutral-500" data-testid={`case-swap-requested-${i.variant_id}`}>Swap requested</span> : <button onClick={() => swap(c, i)} className="text-xs underline underline-offset-4 hover:text-[var(--wine)]" data-testid={`case-swap-${c.id}-${i.variant_id}`}>Swap</button>}</span></li>)}</ul>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button onClick={() => approve(c)} className="rounded-none" data-testid={`suggested-case-approve-${c.id}`}>Approve · pay {money(c.total_pence)} from cellar</Button>
            <Button variant="outline" onClick={() => act(c, "accepted")} className="rounded-none" data-testid={`suggested-case-accept-${c.id}`}>Add to basket instead</Button>
            <button onClick={() => act(c, "dismissed")} className="text-sm text-neutral-500 underline underline-offset-4" data-testid={`suggested-case-dismiss-${c.id}`}>Not this time</button>
          </div>
        </div>
      ))}
    </section>
  );
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const dateWords = (d: SpecialDate) => d.day && d.month ? `${d.day} ${MONTHS[d.month - 1]}${d.year && d.year > 1904 ? ` ${d.year}` : ""}` : shortDate(d.date);

export function SpecialDates() {
  const qc = useQueryClient();
  const { data } = useQuery<SpecialDate[]>({ queryKey: ["dates"], queryFn: () => api.get("/dates").then((r) => r.data) });
  const nm = useAdviserName();
  const blank = { label: "", person: "", day: 0, month: 0, year: "" };
  const [form, setForm] = useState(blank);
  const save = async () => {
    try {
      await api.post("/dates", { label: form.label, person: form.person, day: form.day, month: form.month, year: form.year ? Number(form.year) : null });
      setForm(blank);
      qc.invalidateQueries({ queryKey: ["dates"] });
      toast.success(`Date saved — ${nm.who} will keep an eye out for that vintage`);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const sel = "h-10 w-full border border-input bg-transparent px-3 text-sm";
  const remove = async (id: string) => { await api.delete(`/dates/${id}`); qc.invalidateQueries({ queryKey: ["dates"] }); };
  return (
    <section className="mt-16 grid gap-10 lg:grid-cols-2" data-testid="special-dates">
      <div>
        <div className="eyebrow">Birthday bottles</div>
        <h2 className="mt-2 font-serif text-2xl sm:text-3xl">Dates worth a vintage</h2>
        <p className="mt-3 text-sm text-neutral-600">Tell us the birthdays and anniversaries that matter. {nm.Who} will suggest a bottle from that year in good time, and you can search the cellar by vintage any time.</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <Input placeholder="Occasion (e.g. Dad's 60th)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="rounded-none bg-transparent" data-testid="date-label-input" />
          <Input placeholder="Who (optional)" value={form.person} onChange={(e) => setForm({ ...form, person: e.target.value })} className="rounded-none bg-transparent" data-testid="date-person-input" />
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <select value={form.day} onChange={(e) => setForm({ ...form, day: Number(e.target.value) })} className={sel} data-testid="date-day-select"><option value={0}>Day</option>{Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}</select>
          <select value={form.month} onChange={(e) => setForm({ ...form, month: Number(e.target.value) })} className={sel} data-testid="date-month-select"><option value={0}>Month</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
          <Input inputMode="numeric" placeholder="Year (optional)" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) })} className="rounded-none bg-transparent" data-testid="date-year-input" />
        </div>
        <Button onClick={save} disabled={form.label.length < 2 || !form.day || !form.month || (form.year !== "" && (form.year.length !== 4 || Number(form.year) < 1900))} variant="outline" className="mt-3 rounded-none" data-testid="date-save-button"><CalendarHeart className="mr-2 h-4 w-4" strokeWidth={1.25} />Save date</Button>
      </div>
      <div className="border-t border-border">
        {(data || []).map((d) => (
          <div key={d.id} className="flex items-center justify-between gap-3 border-b border-border py-3 text-sm" data-testid={`date-row-${d.id}`}>
            <div><span className="font-medium">{d.label}</span>{d.person && <span className="text-neutral-500"> · {d.person}</span>}<div className="text-xs text-neutral-500">{dateWords(d)}</div></div>
            <div className="flex items-center gap-3">
              {d.year && d.year > 1904 && <Link to={`/shop?q=${d.year}`} className="text-xs underline underline-offset-4 hover:text-[var(--wine)]" data-testid={`date-vintage-${d.id}`}>Wines from {d.year}</Link>}
              <button onClick={() => remove(d.id)} className="text-neutral-400 hover:text-[var(--wine)]" data-testid={`date-delete-${d.id}`}><Trash2 className="h-4 w-4" strokeWidth={1.25} /></button>
            </div>
          </div>
        ))}
        {data && data.length === 0 && <p className="py-4 text-sm text-neutral-500">No dates yet.</p>}
      </div>
    </section>
  );
}
